<template>
  <div>
    <div class="border-0 border-b border-solid border-gray-a10 py-5 flex justify-center items-center gap-5 h-18">
      <div class="flex justify-between w-80">
        <div class="w-6 flex items-center">
          <vs-icon v-if="displayChevrons.left"
                   class="cursor-pointer"
                   icon="chevron_left"
                   size="1.7rem"
                   @click="changePeriod(false)"
          />
        </div>
        <span class="font-bold text-lg text-black-soft pl-2">{{ periodLabel }}</span>

        <div class="w-6 flex items-center">
          <vs-icon v-if="displayChevrons.right"
                   class="cursor-pointer"
                   icon="chevron_right"
                   size="1.7rem"
                   @click="changePeriod(true)"
          />
        </div>
      </div>
    </div>

    <div class="flex">
      <div class="w-1/3 xl:w-1/4 px-6 pt-8">
        <resolution-filter :data="data"
                           :resolution-filter-data="resolutionFilterData"
                           :selected-resolutions="selectedResolutions"
                           @change="changeResolutionsFilter"
        />
      </div>

      <intents-usages-table id="answersUsagesOverlay"
                            :intents="intents"
                            :total-requests="totalRequests"
                            :get-series-data="getSeriesData"
                            :bot-id="botId"
                            :show-title="true"
                            title="Answer Usages"
                            title-classes="py-6"
                            :route-query-params="routeQuery"
                            full-title-break-point="4xl"
                            variant="sortableOverlay"
                            class="w-2/3 xl:w-3/4 pr-6 border-0 border-t border-solid border-gray-a10"
      />
    </div>
  </div>
</template>

<script>
import isEmpty from 'ramda/src/isEmpty'
import isNil from 'ramda/src/isNil'
import ResolutionFilter from '@/components/analytics/filter/ResolutionFilter'
import IntentsUsagesTable from '@/components/analytics/IntentsUsagesTable'
import requestOutcomeMapper from '@/utils/request-outcome.mapper'
import analyticsFilterModule from '@/store/modules/analytics-filter.module'
import datetimeUtils from '@/utils/datetime.utils'
import AssistanceResolutionsUtils from '@/utils/assistance-resolutions.utils'
import analyticsModule from '@/store/modules/analytics.module'
import answerModule from '@/store/modules/answer.module'
import botModule from '@/store/modules/bot.module'

export default {
  name: 'AssistanceResolutionChartOverlay',

  components: { ResolutionFilter, IntentsUsagesTable },

  props: {
    data: {
      type: Array,
      default: () => []
    },
    resolutionFilterData: {
      type: Object,
      default: () => {}
    },
    botId: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      aggregatedOutcomes: [],
      intents: [],
      stepPeriodProps: {
        valueFrom: 0,
        valueTo: 0,
        name: ''
      },
      periodLabel: '',
      assistanceResolutionsUtils: null
    }
  },

  computed: {
    channels: analyticsFilterModule.getChannels,
    selectedResolutions: analyticsFilterModule.getResolutions,
    requests: analyticsModule.getRequests,
    outcomes: analyticsModule.getOutcomes,
    answers: answerModule.getAnswerList,

    isEmailBot() {
      return botModule.isEmailBot()
    },

    totalRequests() {
      return this.intents.reduce((total, { outcomes }) => {
        return total + outcomes.reduce((sum, { count }) => {
          return sum + count
        }, 0)
      }, 0)
    },

    routeQuery() {
      const { categoryIndex } = this.resolutionFilterData
      const from = this.data[0].data[categoryIndex][0]
      const to = this.data[0].data[categoryIndex + 1]?.[0] - 1

      return {
        dateFrom: datetimeUtils.toMoment(from).format(),
        dateTo: datetimeUtils.toMoment(to).format(),
        assistanceResolutions: this.selectedResolutions.join(',')
      }
    },

    displayChevrons() {
      const { categoryIndex, series } = this.resolutionFilterData
      return {
        left: !isNil(series[0][categoryIndex - 1]),
        right: !isNil(series[0][categoryIndex + 1])
      }
    },

    allResolutions() {
      return this.data.map(({ legendType }) => legendType)
    }
  },

  mounted() {
    this.initSelectedResolutions()
    this.createAssistanceResolutionsUtils()
    this.setStepPeriodProps()
    this.setTableData(this.selectedResolutions)
  },

  methods: {
    createAssistanceResolutionsUtils() {
      const { zoomFrom, zoomTo } = this.resolutionFilterData.zoomRange
      this.assistanceResolutionsUtils = new AssistanceResolutionsUtils(zoomFrom, zoomTo)
    },

    initSelectedResolutions() {
      if (!isEmpty(this.selectedResolutions)) return

      analyticsFilterModule.setResolutions(this.allResolutions)
    },

    setStepPeriodProps() {
      const { currentTimestamp } = this.resolutionFilterData.zoomRange
      this.stepPeriodProps = this.assistanceResolutionsUtils.getPeriodProps(currentTimestamp)
      this.periodLabel = this.assistanceResolutionsUtils.getPeriodLabel(this.stepPeriodProps, true)
    },

    changeResolutionsFilter(resolutions) {
      analyticsFilterModule.setResolutions(resolutions)
      this.setTableData(resolutions)
    },

    setTableData(selectedResolutions) {
      const resolutions = isEmpty(selectedResolutions) ? this.allResolutions : this.selectedResolutions
      const filteredRequests = requestOutcomeMapper.filterRequests(
        this.requests,
        this.routeQuery.dateFrom,
        this.routeQuery.dateTo,
        requestOutcomeMapper.getOutcomesByResolutions(resolutions)
      )

      const answerUsages = requestOutcomeMapper.mapAnswerUsages(
        this.answers,
        filteredRequests,
        true,
        this.isEmailBot
      )
      this.intents = answerUsages.filter(({ answerPublished, usage }) => answerPublished && !!usage)
      this.setAggregatedOutcomes()
    },

    async changePeriod(forward) {
      this.updateResolutions(forward)
      // resolutionFilterData.categoryIndex is updated after the next tick,
      // and setTableData works with not updated value
      await this.$nextTick()

      const newValues = this.assistanceResolutionsUtils.getChangedTimestamps({
        ...this.stepPeriodProps, forward
      })

      this.stepPeriodProps.valueFrom = newValues.newValueFrom
      this.stepPeriodProps.valueTo = newValues.newValueTo
      this.periodLabel = this.assistanceResolutionsUtils.getPeriodLabel(this.stepPeriodProps, true)

      this.setTableData(this.selectedResolutions)
    },

    updateResolutions(forward) {
      const { categoryIndex } = this.resolutionFilterData
      const updatedCategoryIndex = forward ? categoryIndex + 1 : categoryIndex - 1
      this.$emit('update:resolutionFilterData', { ...this.resolutionFilterData, categoryIndex: updatedCategoryIndex })
    },

    getSeriesData(outcomeType) {
      return this.aggregatedOutcomes.map(intentOutcomeTypes => {
        return (intentOutcomeTypes.find(({ legendType }) => legendType === outcomeType)?.count || 0) / this.totalRequests * 100
      })
    },

    setAggregatedOutcomes() {
      this.aggregatedOutcomes = this.intents.map(({ outcomes }) => outcomes)
    }
  }
}
</script>
